/**
 * The overhead layout solver (plan 15 §2.1–§2.2, phase 3) — pure, stateless.
 *
 * Every overhead label group in view (a mob's name + bar + HP number, a
 * player's name + plates + cast bar, an NPC's name) is one item: a box that
 * moves as one. Items are placed in priority order; a higher tier keeps its
 * natural spot and lower tiers move up out of its way, then fade their name
 * if there is still no room within the nudge cap. The result is a vertical
 * nudge and a target name alpha per item — applied as a position and an
 * alpha, never a redraw (OverheadLayout.ts).
 *
 * Deterministic: same items + same previous placements → same result. Sorted
 * by tier, then y, x and id.
 */
import { NAMEPLATE } from '../../../shared/config/nameplate.config';

/**
 * Priority tiers. 0 target · 1 boss/elite · 2 self · 3 party · 4 hostile in
 * combat with you · 5 everyone else. NPCs place as `npc` (after tier 5).
 */
export type OverheadTier = 0 | 1 | 2 | 3 | 4 | 5;
export type OverheadKind = 'mob' | 'player' | 'npc';

export interface OverheadItem {
  id: string;
  kind: OverheadKind;
  tier: OverheadTier;
  /** Column centre, world px. */
  x: number;
  /** Natural top and bottom of the group's box, world px (y grows down). */
  top: number;
  bottom: number;
  /** Box width, world px. */
  width: number;
  /** Distance fade for the name (smart mode), applied when read — not a
   *  layout input, so walking about never forces a re-solve. */
  distanceAlpha?: number;
  /** A mob below full HP: its bar never yields (it is part of a fight). */
  damaged?: boolean;
}

export interface OverheadPlacement {
  /** Upward nudge in px (apply as `y - offsetY`); 0 = natural spot. */
  offsetY: number;
  /** Target alpha for the item's name. */
  nameAlpha: number;
  /** Target alpha for the item's bar(s); absent = 1. Only `smart` yields bars. */
  barAlpha?: number;
}

export interface OverheadLayoutConfig {
  /** Air between two stacked boxes, px. */
  gapY: number;
  /** Horizontal air: boxes closer than this (edge to edge) collide, px. */
  padX: number;
  /** Largest nudge for a mob/player group, px (bounded attachment). */
  maxOffsetY: number;
  /** Largest nudge for an NPC name, px (clears the local player's stack). */
  npcMaxOffsetY: number;
  /** Extra horizontal clearance needed before a raised item drops back to
   *  its natural spot (hysteresis against hopping), px. */
  hysteresisPx: number;
  /** Name alpha when an item of this tier finds no room. Tiers 0–2 never fade. */
  fadeAlpha: Record<OverheadTier, number>;
  /** NPC name alpha when it finds no room. */
  npcFadeAlpha: number;
  /** Settings → Interface "Overhead names" (plan 15 §2.3): `all` never fades
   *  (no room = overlap), `smart` fades by tier, `target` hides tier 3–5
   *  names (target, hovered, bosses and self keep theirs). NPCs: every mode. */
  mode: 'all' | 'smart' | 'target';
  /** `smart`: a quiet mob (untargeted, not a boss, not hovered, not fighting
   *  you, full HP) that would overlap a placed group yields at once instead
   *  of climbing: name to its tier fade, bar to this alpha, and it stops
   *  being an obstacle (the 2026-10-08 follow-up: bars stacking into a block). */
  quietBarAlpha: number;
  /** `smart`: bar alpha of any other group that finds no room (hostile,
   *  damaged, party, players) — dimmed so it does not sit on a neighbour's
   *  name, but kept readable (plan 15 §2.2: "bars never fade below 0.6"). */
  crowdedBarAlpha: number;
}

export const OVERHEAD_LAYOUT_DEFAULTS: OverheadLayoutConfig = {
  gapY: NAMEPLATE.declutter.gapY,
  padX: NAMEPLATE.declutter.padX,
  maxOffsetY: NAMEPLATE.declutter.maxOffsetY,
  npcMaxOffsetY: NAMEPLATE.declutter.npcMaxOffsetY,
  hysteresisPx: NAMEPLATE.declutter.hysteresisPx,
  fadeAlpha: { 0: 1, 1: 1, 2: 1, 3: 0.6, 4: 0.35, 5: 0 },
  npcFadeAlpha: 0.6,
  mode: 'smart',
  quietBarAlpha: 0.25,
  crowdedBarAlpha: 0.6,
};

interface Box { x: number; top: number; bottom: number; half: number }

/** Tier 0 (the target) and tier 2 (self) keep their natural spot. */
function isFixed(item: OverheadItem): boolean {
  return item.kind !== 'npc' && (item.tier === 0 || item.tier === 2);
}

function overlaps(a: Box, b: Box, padX: number, gapY: number): boolean {
  return Math.abs(a.x - b.x) < a.half + b.half + padX
    && a.top < b.bottom + gapY
    && b.top < a.bottom + gapY;
}

function order(a: OverheadItem, b: OverheadItem): number {
  const rank = (i: OverheadItem) => (i.kind === 'npc' ? 6 : i.tier);
  return rank(a) - rank(b)
    || a.bottom - b.bottom
    || a.x - b.x
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export function layoutOverhead(
  items: readonly OverheadItem[],
  previous: ReadonlyMap<string, OverheadPlacement> | null,
  cfg: OverheadLayoutConfig = OVERHEAD_LAYOUT_DEFAULTS,
): Map<string, OverheadPlacement> {
  const out = new Map<string, OverheadPlacement>();
  const placed: Box[] = [];
  const sorted = [...items].sort(order);

  for (const item of sorted) {
    const half = Math.max(0, item.width) / 2;
    const at = (offset: number): Box => ({ x: item.x, top: item.top - offset, bottom: item.bottom - offset, half });
    const blockers = (box: Box, padX: number) => placed.filter((p) => overlaps(box, p, padX, cfg.gapY));

    if (cfg.mode === 'target' && item.kind !== 'npc' && item.tier >= 3) {
      out.set(item.id, { offsetY: 0, nameAlpha: 0 }); // name hidden; not an obstacle
      continue;
    }
    if (isFixed(item)) {
      placed.push(at(0));
      out.set(item.id, { offsetY: 0, nameAlpha: 1 });
      continue;
    }

    const cap = item.kind === 'npc' ? cfg.npcMaxOffsetY : cfg.maxOffsetY;
    const prev = previous?.get(item.id)?.offsetY ?? 0;
    let offset: number | null = null;
    const quiet = cfg.mode === 'smart' && item.kind === 'mob' && item.tier === 5 && !item.damaged;
    if (quiet && blockers(at(0), cfg.padX).length > 0) {
      // A quiet mob in a crowd yields name AND bar and never climbs.
      out.set(item.id, { offsetY: 0, nameAlpha: cfg.fadeAlpha[5], barAlpha: cfg.quietBarAlpha });
      continue;
    }

    // Natural spot — with extra clearance when coming back down (hysteresis).
    if (blockers(at(0), cfg.padX + (prev > 0 ? cfg.hysteresisPx : 0)).length === 0) {
      offset = 0;
    } else if (prev > 0 && prev <= cap && blockers(at(prev), cfg.padX).length === 0) {
      offset = prev; // keep the previous row while it still fits
    } else {
      // Climb: lift just above the highest blocker, until free or past the cap.
      let trial = 0;
      for (let guard = 0; guard < 32; guard++) {
        const hits = blockers(at(trial), cfg.padX);
        if (hits.length === 0) { offset = trial; break; }
        const needed = Math.max(...hits.map((h) => item.bottom - (h.top - cfg.gapY)));
        if (!(needed > trial) || needed > cap) break;
        trial = needed;
      }
    }

    if (offset === null) {
      // No room within the cap: stay attached at the natural spot and yield
      // the name; in `smart` the bar dims too, so it does not sit at full
      // strength on a neighbour's name.
      const fade = cfg.mode === 'all' ? 1 : item.kind === 'npc' ? cfg.npcFadeAlpha : cfg.fadeAlpha[item.tier];
      const bar = cfg.mode === 'smart' && item.kind !== 'npc' && item.tier >= 3 ? cfg.crowdedBarAlpha : 1;
      out.set(item.id, bar < 1 ? { offsetY: 0, nameAlpha: fade, barAlpha: bar } : { offsetY: 0, nameAlpha: fade });
      if (fade > 0) placed.push(at(0));
      continue;
    }
    placed.push(at(offset));
    out.set(item.id, { offsetY: offset, nameAlpha: 1 });
  }
  return out;
}
